import { MAX_TABLE_ROWS } from '@/main/app.constants';
import {
  buildScalingTableRowFromMaterializedTable,
  buildScalingTableRowQuery,
  resolveScalingTableRowName,
} from '@/tableDefinitionEditor/scalingTable.utilities';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import type { NavigableTableCell, WithColumnName } from '@/tableDefinitionEditor/scalingTable.types';
import type { ItemTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';

export const fetchScalingTableDefinition = async (tableDefinitionId: string): Promise<TableDefinitionOutputV1> => {
  const { data } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });
  return data;
};

/**
 * Fetches the whole row of cell values from a scaling table, returning only item cells in `materializedTableRow`.
 * The navigation column's own cell is additionally returned as `navigationColumnCell` whether it's a UUID or TEXT
 * column (CRAB-59914: a TEXT navigation column's cell would otherwise be silently dropped by the item-cell filter
 * in `buildScalingTableRowFromMaterializedTable`).
 * @param tableDefinitionId
 * @param rowId - the id of the cell in the item id or csv item id column
 */
export const getItemsInScalingTableRow = async (
  tableDefinitionId: string,
  rowId: string,
): Promise<{
  materializedTableRow: WithColumnName<ItemTableCell>[];
  navigationColumnCell?: NavigableTableCell;
  scalingTableDefinition: TableDefinitionOutputV1;
}> => {
  const scalingTableDefinition = await fetchScalingTableDefinition(tableDefinitionId);
  const { columnNames, propertyRequests, filter, tableJoinInputs } = buildScalingTableRowQuery(
    scalingTableDefinition,
    rowId,
  );

  // Calls the SDK client directly (rather than materializedTable.utilities' getMaterializedTable) to avoid a
  // circular import
  const { data: tableData } = await sqTablesApi.queryTableData(
    {
      limit: MAX_TABLE_ROWS,
      columnsToInclude: columnNames,
      propertiesToInclude: propertyRequests,
      includeOverrides: true,
      tablesToJoin: tableJoinInputs,
      sorts: [],
      filter,
    },
    { tableId: tableDefinitionId },
  );

  const { materializedTableRow, navigationColumnCell } = buildScalingTableRowFromMaterializedTable(
    scalingTableDefinition,
    tableData,
  );

  return { materializedTableRow, navigationColumnCell, scalingTableDefinition };
};

/**
 * Fetches a scaling table row's materialized data and resolves its display name via `resolveScalingTableRowName`.
 * For callers (e.g. the scaling row swap flow) that only have a tableId/rowId and haven't already fetched the row.
 */
export const getScalingTableRowDisplayName = async (
  tableDefinitionId: string,
  rowId: string,
): Promise<string | undefined> => {
  const { materializedTableRow, navigationColumnCell, scalingTableDefinition } = await getItemsInScalingTableRow(
    tableDefinitionId,
    rowId,
  );
  return resolveScalingTableRowName(scalingTableDefinition, materializedTableRow, navigationColumnCell, rowId);
};
